/**
 * S5 — Rep "My leads" detail (`/leads/:taskId`). A read-only overview of one
 * handoff task — NOT a reply inbox. Shows the resolved lead person/company name
 * (headline) with the UUID ref kept as a secondary copyable id, the agent's
 * handoff context, the first-touch deadline and status, plus two actions for an
 * open task: "Mark worked" and "Escalate to AM" (both carry the entity `version`
 * for the optimistic lock; a 409 reloads + warns, never overwrites).
 *
 * Honest posture: the rep contacts the lead from their own sales email — the
 * platform does not host that conversation (surfaced as a note). Names are
 * resolved server-side from the refs; when a name is missing it falls back to the
 * UUID ref honestly — never a fabricated contact.
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S5.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Copy2Line,
  EmptyState,
  InlineAlert,
  Skeleton,
  toast,
} from '@teivor/shared-ui';
import { formatDateTime } from '@teivor/shared-utils';
import { ConflictError } from '@teivor/shared-utils/api';

import { PageContainer } from '../../../components/PageContainer';

import { useEscalateMutation, useMarkWorkedMutation, useRepTaskQuery } from './queries';
import type { RepTask } from './types';
import { dueState, statusBadge } from './shared';

function useLocale(): 'cs-CZ' | 'en-US' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en-US' : 'cs-CZ';
}

// ─── Copyable reference (Martian-Mono id + copy affordance) ──────────────────

function CopyRef({ label, value, name }: { label: string; value: string; name?: string | null }) {
  const { t } = useTranslation();
  const onCopy = React.useCallback(() => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => toast.success(t('com.rep.copied', 'Zkopírováno / Copied')))
      .catch(() => toast.error(t('com.rep.copy_failed', 'Kopírování selhalo / Copy failed')));
  }, [value, t]);

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)]">{label}</span>
        {/* Name is the headline when resolved; the UUID ref is the small secondary mono line. */}
        {name ? (
          <span className="truncate text-[14px] leading-[20px] text-[var(--tvl-ink)]">{name}</span>
        ) : null}
        <span
          className="truncate text-[12px] leading-[16px] text-[var(--tvl-ink-3)]"
          style={{ fontFamily: 'var(--tvl-font-mono)' }}
        >
          {value}
        </span>
      </div>
      <button
        type="button"
        onClick={onCopy}
        aria-label={t('com.rep.copy', 'Kopírovat / Copy')}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--tvl-r2)] text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)] outline-none focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]"
      >
        <Copy2Line size={16} color="currentColor" aria-hidden />
      </button>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function RepLeadDetailPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();

  const { data: task, isLoading, isError, refetch } = useRepTaskQuery(taskId);
  const markWorked = useMarkWorkedMutation();
  const escalate = useEscalateMutation();

  const [dialog, setDialog] = React.useState<'worked' | 'escalate' | null>(null);

  const onConflict = React.useCallback(() => {
    toast.error(
      t('com.rep.conflict', 'Úloha se mezitím změnila — načítám znovu / The task changed — reloading'),
    );
    void refetch();
  }, [refetch, t]);

  const doWorked = React.useCallback(
    (current: RepTask) => {
      markWorked.mutate(
        { taskId: current.id, version: current.version },
        {
          onSuccess: () => {
            setDialog(null);
            toast.success(t('com.rep.worked_toast', 'Označeno jako kontaktováno / Marked as worked'));
          },
          onError: (err) => {
            setDialog(null);
            if (err instanceof ConflictError) onConflict();
            else toast.error(t('com.rep.action_error', 'Akce se nezdařila / Action failed'));
          },
        },
      );
    },
    [markWorked, onConflict, t],
  );

  const doEscalate = React.useCallback(
    (current: RepTask) => {
      escalate.mutate(
        { taskId: current.id, version: current.version },
        {
          onSuccess: () => {
            setDialog(null);
            toast.success(t('com.rep.escalated_toast', 'Předáno account managerovi / Escalated to your account manager'));
          },
          onError: (err) => {
            setDialog(null);
            if (err instanceof ConflictError) onConflict();
            else toast.error(t('com.rep.action_error', 'Akce se nezdařila / Action failed'));
          },
        },
      );
    },
    [escalate, onConflict, t],
  );

  return (
    <PageContainer width="narrow">
      <button
        type="button"
        onClick={() => navigate('/leads')}
        className="self-start rounded-[var(--tvl-r1)] text-[13px] leading-[18px] text-[var(--tvl-ink-2)] hover:text-[var(--tvl-ink)] outline-none focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-2"
      >
        ← {t('com.rep.back', 'Zpět na leady / Back to leads')}
      </button>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : isError || !task ? (
        <EmptyState
          variant="error-loading"
          title={t('com.rep.detail_error', 'Lead se nepodařilo načíst / Could not load this lead')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('common.retry', 'Zkusit znovu / Retry')}
            </Button>
          }
        />
      ) : (
        <RepLeadDetail
          task={task}
          locale={locale}
          onMarkWorked={() => setDialog('worked')}
          onEscalate={() => setDialog('escalate')}
        />
      )}

      <ConfirmDialog
        open={dialog === 'worked'}
        onOpenChange={(open) => setDialog(open ? 'worked' : null)}
        title={t('com.rep.worked_title', 'Označit jako kontaktováno? / Mark as worked?')}
        description={t(
          'com.rep.worked_desc',
          'Potvrďte, že jste leada oslovili ze své prodejní schránky. / Confirm you have contacted this lead from your sales email.',
        )}
        confirmLabel={t('com.rep.worked_yes', 'Ano, kontaktováno / Yes, worked')}
        cancelLabel={t('common.cancel', 'Zrušit / Cancel')}
        onConfirm={() => task && doWorked(task)}
        loading={markWorked.isPending}
      />

      <ConfirmDialog
        open={dialog === 'escalate'}
        onOpenChange={(open) => setDialog(open ? 'escalate' : null)}
        title={t('com.rep.escalate_title', 'Předat account managerovi? / Escalate to AM?')}
        description={t(
          'com.rep.escalate_desc',
          'Tuto úlohu převezme váš account manager. / Your account manager will take over this task.',
        )}
        confirmLabel={t('com.rep.escalate_yes', 'Ano, předat / Yes, escalate')}
        cancelLabel={t('common.cancel', 'Zrušit / Cancel')}
        onConfirm={() => task && doEscalate(task)}
        loading={escalate.isPending}
      />
    </PageContainer>
  );
}

// ─── Detail body ─────────────────────────────────────────────────────────────

function RepLeadDetail({
  task,
  locale,
  onMarkWorked,
  onEscalate,
}: {
  task: RepTask;
  locale: 'cs-CZ' | 'en-US';
  onMarkWorked: () => void;
  onEscalate: () => void;
}) {
  const { t } = useTranslation();
  const spec = statusBadge(task.taskStatus);
  const state = dueState(task.firstTouchDueAt, task.taskStatus);
  const danger = state === 'overdue' || state === 'at_risk';
  const isOpen = task.taskStatus === 'open';

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <CardTitle>
                {task.personName ?? t('com.rep.detail_title', 'Lead k oslovení / Lead to contact')}
              </CardTitle>
              {task.personName && (task.personTitle || task.companyName) ? (
                <span className="text-[13px] leading-[18px] text-[var(--tvl-ink-2)]">
                  {[task.personTitle, task.companyName].filter(Boolean).join(' · ')}
                </span>
              ) : task.companyName ? (
                <span className="text-[13px] leading-[18px] text-[var(--tvl-ink-2)]">{task.companyName}</span>
              ) : null}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)]">
                {t('com.rep.col.due', 'Kontaktovat do / Contact by')}
              </span>
              <span
                className="text-[14px] font-medium leading-[20px] tabular-nums"
                style={{ color: danger ? 'var(--tvl-accent)' : 'var(--tvl-ink)' }}
              >
                {formatDateTime(task.firstTouchDueAt, locale)}
              </span>
            </div>
          </div>
          <Badge variant={spec.variant}>{t(spec.key, spec.fallback)}</Badge>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/* Actions — open tasks only */}
          {isOpen ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={onMarkWorked}>
                {t('com.rep.markWorked', 'Označit jako kontaktováno / Mark as worked')}
              </Button>
              <Button variant="secondary" onClick={onEscalate}>
                {t('com.rep.escalate', 'Předat AM / Escalate to AM')}
              </Button>
            </div>
          ) : task.taskStatus === 'worked' && task.workedAt ? (
            <InlineAlert variant="success">
              {t('com.rep.worked_at', 'Kontaktováno / Worked')} {formatDateTime(task.workedAt, locale)}
            </InlineAlert>
          ) : task.taskStatus === 'escalated_to_am' ? (
            <InlineAlert variant="warning">
              {t('com.rep.escalated_notice', 'Tato úloha byla předána vašemu account managerovi. / This task was escalated to your account manager.')}
            </InlineAlert>
          ) : null}

          {/* Honest note — the rep writes from their own sales email */}
          <InlineAlert variant="info">
            {t(
              'com.rep.own_email_note',
              'Leada kontaktujte ze své vlastní prodejní schránky — platforma tuto konverzaci nehostí. / Contact the lead from your own sales email — the platform does not host this conversation.',
            )}
          </InlineAlert>
        </CardContent>
      </Card>

      {/* Handoff context — the substance */}
      <Card>
        <CardHeader>
          <CardTitle>{t('com.rep.context_title', 'Kontext předání / Handoff context')}</CardTitle>
        </CardHeader>
        <CardContent>
          {task.contextSummary?.trim() ? (
            <p className="whitespace-pre-wrap text-[14px] leading-[22px] text-[var(--tvl-ink)]">
              {task.contextSummary}
            </p>
          ) : (
            <EmptyState
              variant="empty"
              title={t('com.rep.noContext', 'Bez kontextu / No context')}
              description={t(
                'com.rep.noContext_desc',
                'Agent nepředal žádné shrnutí konverzace. / The agent provided no conversation summary.',
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Lead references — resolved name as headline, the copyable id as a secondary ref. */}
      <Card>
        <CardHeader>
          <CardTitle>{t('com.rep.refs_title', 'Identifikátory / References')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <CopyRef
            label={t('com.rep.ref.person', 'Lead (osoba) / Lead (person)')}
            value={task.leadPersonRef}
            name={task.personName}
          />
          {/* Company ref is nullable (company-less handoff) — omit the row honestly. */}
          {task.leadCompanyRef ? (
            <CopyRef
              label={t('com.rep.ref.company', 'Firma / Company')}
              value={task.leadCompanyRef}
              name={task.companyName}
            />
          ) : null}
          <CopyRef label={t('com.rep.ref.conversation', 'Konverzace / Conversation')} value={task.conversationRef} />
        </CardContent>
      </Card>
    </>
  );
}
